import { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { useWorkerStore } from '@core/worker/useWorkerStore';
import { useJobApplicationStore } from '@core/jobApplication/useJobApplicationStore';
import type { User } from '@core/generated/gql-types';
import type { WorkerDetailView } from '@/modules/workers/views/workerDetail.view';

type UseWorkerDetailData = {
  worker: User | null | undefined;
  isLoading: boolean;
  error: string | null;
  refetchWorker: () => Promise<void>;
};

export const useWorkerDetailData = (
  view: WorkerDetailView,
  workerId: string | undefined,
): UseWorkerDetailData => {
  const { fetchWorkerById, getWorkerById } = useWorkerStore();
  const { fetchJobApplicationsForUser } = useJobApplicationStore();
  const worker = workerId ? getWorkerById(workerId) : null;
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const refetchWorker = async () => {
    if (!workerId || !view.query.worker.fetch) return;
    await fetchWorkerById(workerId);
  };

  useEffect(() => {
    const fetchWorker = async () => {
      if (!view.query.worker.fetch || !workerId) {
        setIsLoading(false);
        return;
      }

      try {
        setIsLoading(true);
        await fetchWorkerById(workerId);
        setError(null);
      } catch (fetchError) {
        console.error(`Error fetching worker: ${fetchError}`);
        setError(fetchError instanceof Error ? fetchError.message : 'Error');
        toast.error(
          `Fetching worker failed: ${fetchError instanceof Error ? fetchError.message : 'Error'}`,
          {
            position: 'bottom-left',
            autoClose: 5000,
            toastId: 'fetch-worker-error',
          },
        );
      } finally {
        setIsLoading(false);
      }
    };

    void fetchWorker();
  }, [workerId, fetchWorkerById, view.query.worker.fetch]);

  useEffect(() => {
    const fetchApplications = async () => {
      if (!view.query.applications.fetch || !worker?.id) return;

      try {
        await fetchJobApplicationsForUser(worker.id);
      } catch (fetchError) {
        console.error(`Error getting job applications: ${fetchError}`);
        toast.error(
          `Fetching job applications failed: ${fetchError instanceof Error ? fetchError.message : 'Error'}`,
          {
            position: 'bottom-left',
            autoClose: 5000,
            toastId: 'fetch-applications-error',
          },
        );
      }
    };

    void fetchApplications();
  }, [worker, fetchJobApplicationsForUser, view.query.applications.fetch]);

  return {
    worker,
    isLoading,
    error,
    refetchWorker,
  };
};
